import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { useMemo } from 'react';
import { JourneyOtpInput, JOURNEY_OTP_LENGTH } from '~/components/StandardJourney';
import type { RenewalContract } from '~/services/modules/renewal';
import RenewalContractSummary from '../components/RenewalContractSummary';
import { RENEWAL_APP_IDS } from '../renewalAppIds';
import { getRenewalChannelOptions, type RenewalChannel } from '../renewalChannel';
import type { RenewalCodeStatus } from '../renewalSteps';

export type { RenewalCodeStatus };

type Props = {
  contract: RenewalContract;
  channel: RenewalChannel;
  /** Outro canal disponível para «Receber por …»; `null` quando o segurado só tem um contato. */
  alternateChannel: RenewalChannel | null;
  code: string;
  status: RenewalCodeStatus;
  resending: boolean;
  onCodeChange: (code: string) => void;
  onVerify: () => void;
  onResend: () => void;
  onSwitchChannel: () => void;
  onChangeContact: () => void;
};

const CHANNEL_NAME: Record<RenewalChannel, string> = { email: 'e-mail', sms: 'SMS' };

/**
 * Etapa 2 da renovação 2026: código de verificação (Figma 2086:6702 enviado, 2086:6915 digitando,
 * 2086:6812 incorreto, 2086:7008 verificando e 2086:7225 verificado).
 */
export default function RenewalVerifyStep({
  contract,
  channel,
  alternateChannel,
  code,
  status,
  resending,
  onCodeChange,
  onVerify,
  onResend,
  onSwitchChannel,
  onChangeContact
}: Props) {
  const contact = useMemo(
    () => getRenewalChannelOptions(contract.insured ?? {}).find((option) => option.channel === channel)?.contact ?? '',
    [channel, contract.insured]
  );
  const verifying = status === 'verifying';
  const verified = status === 'success';
  const locked = verifying || verified;
  const canVerify = code.length === JOURNEY_OTP_LENGTH && !locked;

  return (
    <Box
      data-app-id={RENEWAL_APP_IDS.stepVerify}
      data-testid="renewal-step-verify"
      sx={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}
    >
      <Typography component="h1" sx={{ textAlign: 'center', width: '100%' }} variant="h5">
        Renovação do contrato
      </Typography>

      <RenewalContractSummary appId={RENEWAL_APP_IDS.contractSummary} contract={contract} />

      <Box
        data-app-id={RENEWAL_APP_IDS.codeSentBanner}
        sx={(theme) => ({
          alignItems: 'center',
          bgcolor: theme.customColors.backgroundTertiary,
          border: '1px solid',
          borderColor: 'primary.light',
          borderRadius: '8px',
          display: 'flex',
          gap: '8px',
          justifyContent: 'space-between',
          p: '8px',
          width: '100%'
        })}
      >
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px', minWidth: 0 }}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderRadius: '100px',
              color: 'primary.light',
              display: 'flex',
              flexShrink: 0,
              height: 40,
              justifyContent: 'center',
              width: 40
            }}
          >
            {channel === 'sms' ? <CommentOutlinedIcon /> : <MailOutlineIcon />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: 'text.primary' }} variant="body2">
              Enviamos um código para{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {contact}
              </Box>
            </Typography>
            <Typography sx={{ color: 'text.secondary', display: 'block' }} variant="caption">
              Ele expira em 10 minutos
            </Typography>
          </Box>
        </Box>
        <Button
          data-app-id={RENEWAL_APP_IDS.changeContact}
          disabled={locked}
          onClick={onChangeContact}
          size="small"
          sx={(theme) => ({ color: theme.customColors.tertiaryMain, flexShrink: 0 })}
          variant="text"
        >
          Alterar
        </Button>
      </Box>

      <Typography sx={{ textAlign: 'center', width: '100%' }} variant="subtitle1">
        Digite o código de 6 dígitos
      </Typography>

      <JourneyOtpInput
        appId={RENEWAL_APP_IDS.codeInput}
        disabled={locked}
        focusOnMount
        message={status === 'error' ? 'Código incorreto.' : verified ? 'Código verificado.' : undefined}
        onChange={onCodeChange}
        status={status === 'error' ? 'error' : verified ? 'success' : 'idle'}
        value={code}
      />

      {verified ? null : (
        <Box
          sx={{
            alignItems: 'center',
            columnGap: '24px',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            rowGap: '4px'
          }}
        >
          <Button
            data-app-id={RENEWAL_APP_IDS.resendCode}
            disabled={resending || verifying}
            onClick={onResend}
            sx={(theme) => ({ color: theme.customColors.tertiaryMain })}
            variant="text"
          >
            {resending ? 'Reenviando…' : 'Reenviar código'}
          </Button>
          {alternateChannel ? (
            <>
              <Divider flexItem orientation="vertical" sx={{ display: { sm: 'block', xs: 'none' }, my: '6px' }} />
              <Button
                data-app-id={RENEWAL_APP_IDS.switchChannel}
                disabled={resending || verifying}
                onClick={onSwitchChannel}
                sx={(theme) => ({ color: theme.customColors.tertiaryMain })}
                variant="text"
              >
                Receber por {CHANNEL_NAME[alternateChannel]}
              </Button>
            </>
          ) : null}
        </Box>
      )}

      <Button
        data-app-id={RENEWAL_APP_IDS.verifyCode}
        disabled={!canVerify}
        fullWidth
        onClick={onVerify}
        size="large"
        startIcon={verifying ? <CircularProgress aria-hidden color="inherit" size={18} /> : undefined}
        variant="contained"
      >
        {verifying ? 'Verificando' : 'Verificar e continuar'}
      </Button>
    </Box>
  );
}
